import {
  OFFLINE_SCHEMA_VERSION,
  PROCESSING_STALE_MS,
  SYNC_STATE,
  classifySyncFailure,
  isFailedSyncState,
  isPendingSyncState,
  isSyncItemDue,
  normalizeLegacySyncItem,
  syncFailureLabel,
} from "./offline-queue-core.js";
import {
  SYNCED_MEDIA_RETENTION_DAYS,
  evaluateStorageCapacity,
  isSyncedMediaBlobCleanupEligible,
  retainedMediaBytes,
  summarizeRetainedMedia,
} from "./driver-storage-policy.js";

const DB_NAME = "sedulur-dalan-offline";
const DB_VERSION = OFFLINE_SCHEMA_VERSION;
const CHANGE_EVENT = "sedulur-dalan-sync-change";
const SESSION_RECOVERY_EVENT = "sedulur-dalan-session-recovery-required";
const QUEUE_LOCK = "sedulur-dalan-queue-processor";
const MEDIA_LOCK = "sedulur-dalan-media-processor";
const ROUTE_DRAFT_PREFIX = "driver:route-draft:";
const REQUEST_TIMEOUT_MS = 45_000;
const SYNC_BROADCAST_CHANNEL = "sedulur-dalan-sync-v1";
let activeFlushPromise = null;
let activeMediaPromise = null;

const STORAGE_CAPACITY_MESSAGE = "Penyimpanan perangkat hampir penuh. Data dan foto yang belum tersinkron tidak dihapus. Sambungkan internet dan sinkronkan, lalu kosongkan ruang perangkat sebelum mengambil foto baru.";

function nowIso() {
  return new Date().toISOString();
}

function online() {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function createIndexes(store) {
  if (!store.indexNames.contains("createdAt")) store.createIndex("createdAt", "createdAt");
  if (!store.indexNames.contains("syncStatus")) store.createIndex("syncStatus", "syncStatus");
  if (!store.indexNames.contains("nextAttemptAt")) store.createIndex("nextAttemptAt", "nextAttemptAt");
}

function migrateStore(store, kind) {
  createIndexes(store);
  const cursorRequest = store.openCursor();
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    cursor.update(normalizeLegacySyncItem(cursor.value, { kind }));
    cursor.continue();
  };
}

function openDatabase() {
  if (!("indexedDB" in globalThis)) return Promise.reject(new Error("IndexedDB tidak tersedia."));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      const upgrade = request.transaction;
      if (!db.objectStoreNames.contains("cache")) db.createObjectStore("cache", { keyPath: "key" });
      const queue = db.objectStoreNames.contains("queue")
        ? upgrade.objectStore("queue")
        : db.createObjectStore("queue", { keyPath: "id" });
      const media = db.objectStoreNames.contains("media")
        ? upgrade.objectStore("media")
        : db.createObjectStore("media", { keyPath: "id" });
      migrateStore(queue, "queue");
      migrateStore(media, "media");
    };
    request.onblocked = () => reject(new Error("Pembaruan penyimpanan offline menunggu tab lama ditutup."));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function transaction(storeName, mode, callback) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result;
    try {
      result = callback(store);
    } catch (error) {
      db.close();
      reject(error);
      return;
    }
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

function requestValue(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readAll(storeName) {
  const db = await openDatabase();
  try {
    const tx = db.transaction(storeName, "readonly");
    return (await requestValue(tx.objectStore(storeName).getAll())) || [];
  } finally {
    db.close();
  }
}

let syncBroadcastChannel = null;
if (typeof globalThis.BroadcastChannel === "function") {
  syncBroadcastChannel = new BroadcastChannel(SYNC_BROADCAST_CHANNEL);
  syncBroadcastChannel.addEventListener("message", () => {
    globalThis.dispatchEvent?.(new CustomEvent(CHANGE_EVENT));
  });
}

function emitChange({ broadcast = true } = {}) {
  globalThis.dispatchEvent?.(new CustomEvent(CHANGE_EVENT));
  if (broadcast) syncBroadcastChannel?.postMessage({ type: "SYNC_STATE_CHANGED", at: nowIso() });
}

function requestSessionRecovery() {
  globalThis.dispatchEvent?.(new CustomEvent(SESSION_RECOVERY_EVENT));
}

function logSync(event, item, extra = {}) {
  if (!import.meta.env?.DEV) return;
  console.info("[driver-sync]", {
    event,
    queueId: item?.id,
    operationType: item?.action || item?.mediaKind,
    fleetOrderId: item?.fleetOrderId || item?.payload?.fleetOrderId || null,
    stopId: item?.stopKey || item?.payload?.stopKey || null,
    attemptCount: item?.attemptCount || 0,
    syncStatus: item?.syncStatus,
    ...extra,
  });
}

async function fetchWithTimeout(path, options, timeoutMs = REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException("Request timeout", "TimeoutError")), timeoutMs);
  try {
    return await fetch(path, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function encodeCaptureMetadataHeader(value = null) {
  if (!value || typeof value !== "object") return "";
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/g, "");
}

function replaceMediaReferences(value, mappings) {
  if (typeof value === "string") return mappings.get(value) || value;
  if (Array.isArray(value)) return value.map((item) => replaceMediaReferences(item, mappings));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, replaceMediaReferences(item, mappings)]));
  }
  return value;
}

function payloadContainsReference(value, references) {
  if (typeof value === "string") return references.has(value);
  if (Array.isArray(value)) return value.some((item) => payloadContainsReference(item, references));
  if (value && typeof value === "object") return Object.values(value).some((item) => payloadContainsReference(item, references));
  return false;
}

function shouldAttempt(item, { force = false, onlyId = null, includeRecoverable = false } = {}) {
  if (onlyId && item.id !== onlyId) return false;
  if ([SYNC_STATE.SYNCED, SYNC_STATE.SUPERSEDED, SYNC_STATE.FAILED_PERMANENT].includes(item.syncStatus)) return false;
  if (item.syncStatus === SYNC_STATE.FAILED_RECOVERABLE) return force || includeRecoverable;
  return force || isSyncItemDue(item);
}

function attemptFields(item) {
  const nextAttempt = Number(item.attemptCount || item.retryCount || 0) + 1;
  return {
    attemptCount: nextAttempt,
    retryCount: nextAttempt,
    lifetimeAttemptCount: Number(item.lifetimeAttemptCount || item.attemptCount || item.retryCount || 0) + 1,
    lastAttemptAt: nowIso(),
  };
}

async function parseErrorPayload(response) {
  return response.json().catch(() => ({}));
}

async function updateQueueItem(item) {
  await transaction("queue", "readwrite", (store) => store.put({ ...item, updatedAt: nowIso() }));
}

async function updateMediaItem(item) {
  await transaction("media", "readwrite", (store) => store.put({ ...item, updatedAt: nowIso() }));
}

function isQuotaExceededError(error) {
  return error?.name === "QuotaExceededError" || Number(error?.code) === 22;
}

function storageCapacityError(cause = null) {
  const error = new Error(STORAGE_CAPACITY_MESSAGE, cause ? { cause } : undefined);
  error.name = "DriverStorageCapacityError";
  error.code = "DRIVER_STORAGE_CAPACITY_LOW";
  return error;
}

async function deleteQueueItem(id) {
  await transaction("queue", "readwrite", (store) => store.delete(id));
}

async function repairStore(storeName, kind) {
  const items = await readAll(storeName);
  if (!items.length) return;
  const normalized = items.map((item) => normalizeLegacySyncItem(item, { kind }));
  await transaction(storeName, "readwrite", (store) => normalized.forEach((item) => store.put(item)));
}

export async function initializeDriverOfflineStore() {
  const db = await openDatabase();
  db.close();
  await repairStore("queue", "queue");
  await repairStore("media", "media");
  await cleanupExpiredSyncedDriverMedia();
  emitChange();
  return driverQueueStatus();
}

export async function cleanupExpiredSyncedDriverMedia({ now = Date.now() } = {}) {
  const media = await listDriverMedia();
  const eligible = media.filter((item) => isSyncedMediaBlobCleanupEligible(item, { now }));
  if (!eligible.length) return { cleanedCount: 0, cleanedBytes: 0 };
  let cleanedBytes = 0;
  await transaction("media", "readwrite", (store) => {
    eligible.forEach((item) => {
      cleanedBytes += retainedMediaBytes(item);
      const metadata = { ...item };
      delete metadata.file;
      delete metadata.originalFile;
      store.put({
        ...metadata,
        blobRetained: false,
        blobCleanedAt: new Date(Number(now)).toISOString(),
        retentionDays: SYNCED_MEDIA_RETENTION_DAYS,
        updatedAt: new Date(Number(now)).toISOString(),
      });
    });
  });
  emitChange();
  return { cleanedCount: eligible.length, cleanedBytes };
}

export async function getDriverStorageDiagnostics({ now = Date.now() } = {}) {
  const media = await listDriverMedia();
  const retained = summarizeRetainedMedia(media, now);
  let estimate = {};
  try {
    estimate = await globalThis.navigator?.storage?.estimate?.() || {};
  } catch {
    estimate = {};
  }
  return {
    ...retained,
    usageBytes: Number.isFinite(Number(estimate.usage)) ? Number(estimate.usage) : null,
    quotaBytes: Number.isFinite(Number(estimate.quota)) ? Number(estimate.quota) : null,
    retentionDays: SYNCED_MEDIA_RETENTION_DAYS,
  };
}

async function ensureDriverMediaCapacity(incomingBytes) {
  await cleanupExpiredSyncedDriverMedia();
  let estimate = {};
  try {
    estimate = await globalThis.navigator?.storage?.estimate?.() || {};
  } catch {
    return;
  }
  if (!evaluateStorageCapacity({
    usage: estimate.usage,
    quota: estimate.quota,
    incomingBytes,
  }).accepted) {
    throw storageCapacityError();
  }
}

export async function putDriverCache(key, value) {
  try {
    await transaction("cache", "readwrite", (store) => store.put({ key, value, updatedAt: nowIso() }));
  } catch {
    // Cache failure must never block an online driver action.
  }
}

export async function getDriverCache(key) {
  try {
    const db = await openDatabase();
    const tx = db.transaction("cache", "readonly");
    const result = await requestValue(tx.objectStore("cache").get(key));
    db.close();
    return result?.value ?? null;
  } catch {
    return null;
  }
}

export async function deleteDriverCache(key) {
  try {
    await transaction("cache", "readwrite", (store) => store.delete(key));
  } catch {
    // Local cache cleanup must not block driver operations.
  }
}

function routeDraftKey(fleetOrderId) {
  return `${ROUTE_DRAFT_PREFIX}${String(fleetOrderId || "").trim()}`;
}

export async function getDriverRouteDraft(fleetOrderId) {
  if (!fleetOrderId) return null;
  return getDriverCache(routeDraftKey(fleetOrderId));
}

export async function saveDriverRouteDraft(fleetOrderId, draft = {}) {
  if (!fleetOrderId) throw new Error("Fleet Order tidak tersedia untuk menyimpan draft rute.");
  const timestamp = nowIso();
  const value = {
    fleetOrderId: String(fleetOrderId),
    stopKeys: Array.isArray(draft.stopKeys) ? draft.stopKeys.map(String) : [],
    expectedRouteVersion: Number(draft.expectedRouteVersion || 1),
    method: draft.method || "MANUAL_DRAG",
    anchor: draft.anchor || null,
    acknowledgeLoadingImpact: Boolean(draft.acknowledgeLoadingImpact),
    idempotencyKey: draft.idempotencyKey || null,
    syncStatus: draft.syncStatus || SYNC_STATE.PENDING,
    lastErrorCode: draft.lastErrorCode || null,
    lastErrorMessage: draft.lastErrorMessage || null,
    queuedAt: draft.queuedAt || timestamp,
    updatedAt: timestamp,
  };
  await putDriverCache(routeDraftKey(fleetOrderId), value);
  emitChange();
  return value;
}

export async function clearDriverRouteDraft(fleetOrderId, { idempotencyKey = null } = {}) {
  if (!fleetOrderId) return false;
  const current = await getDriverRouteDraft(fleetOrderId);
  if (!current) return false;
  if (idempotencyKey && current.idempotencyKey && current.idempotencyKey !== idempotencyKey) return false;
  await deleteDriverCache(routeDraftKey(fleetOrderId));
  emitChange();
  return true;
}

export async function markDriverRouteDraftConflict(fleetOrderId, update = {}) {
  const current = await getDriverRouteDraft(fleetOrderId);
  if (!current) return null;
  if (update.idempotencyKey && current.idempotencyKey && current.idempotencyKey !== update.idempotencyKey) return current;
  const next = {
    ...current,
    syncStatus: "CONFLICT",
    lastErrorCode: update.lastErrorCode || current.lastErrorCode || "DRIVER_ROUTE_VERSION_CONFLICT",
    lastErrorMessage: update.lastErrorMessage || current.lastErrorMessage || "Urutan rute sudah berubah.",
    updatedAt: nowIso(),
  };
  await putDriverCache(routeDraftKey(fleetOrderId), next);
  emitChange();
  return next;
}

export async function discardPendingDriverRoute(fleetOrderId) {
  if (!fleetOrderId) return driverQueueStatus();
  const timestamp = nowIso();
  const items = await listDriverQueue();
  const matches = items.filter((item) => item.action === "REORDER_STOPS"
    && String(item.fleetOrderId || item.payload?.fleetOrderId || "") === String(fleetOrderId)
    && ![SYNC_STATE.SYNCED, SYNC_STATE.SUPERSEDED].includes(item.syncStatus));
  if (matches.length) {
    await transaction("queue", "readwrite", (store) => matches.forEach((item) => store.put({
      ...item,
      syncStatus: SYNC_STATE.SUPERSEDED,
      recoverability: null,
      nextAttemptAt: null,
      processingStartedAt: null,
      supersededAt: timestamp,
      updatedAt: timestamp,
    })));
  }
  await clearDriverRouteDraft(fleetOrderId);
  emitChange();
  return driverQueueStatus();
}

export async function queueDriverAction({ id, entityType, action, path, method, payload, dedupeKey = null }) {
  const timestamp = nowIso();
  const item = normalizeLegacySyncItem({
    id,
    localTransactionId: id,
    idempotencyKey: payload.idempotencyKey,
    entityType,
    action,
    path,
    method,
    payload,
    dedupeKey,
    fleetOrderId: payload.fleetOrderId || null,
    stopKey: payload.stopKey || null,
    createdAt: timestamp,
    updatedAt: timestamp,
    attemptCount: 0,
    syncStatus: SYNC_STATE.PENDING,
  }, { kind: "queue", now: timestamp });

  const existing = dedupeKey ? await listDriverQueue() : [];
  await transaction("queue", "readwrite", (store) => {
    existing
      .filter((entry) => entry.dedupeKey === dedupeKey
        && entry.id !== item.id
        && ![SYNC_STATE.SYNCED, SYNC_STATE.SUPERSEDED].includes(entry.syncStatus))
      .forEach((entry) => store.put({
        ...entry,
        syncStatus: SYNC_STATE.SUPERSEDED,
        recoverability: null,
        nextAttemptAt: null,
        processingStartedAt: null,
        supersededAt: timestamp,
        supersededBy: item.id,
        updatedAt: timestamp,
      }));
    store.put(item);
  });
  logSync("queued", item, { dedupeKey });
  emitChange();
  return item;
}

export async function listDriverQueue() {
  try {
    return (await readAll("queue")).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
  } catch {
    return [];
  }
}

export async function listDriverMedia() {
  try {
    return (await readAll("media")).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
  } catch {
    return [];
  }
}

async function processPendingDriverMedia(options = {}) {
  if (!online()) return { mappings: new Map(), ...(await driverQueueStatus()) };
  const items = await listDriverMedia();
  const mappings = new Map(
    items.filter((item) => item.syncStatus === SYNC_STATE.SYNCED && item.serverMediaId)
      .map((item) => [item.id, item.serverMediaId]),
  );

  for (const item of items.filter((entry) => shouldAttempt(entry, options))) {
    const attempts = attemptFields(item);
    const processing = {
      ...item,
      ...attempts,
      syncStatus: SYNC_STATE.PROCESSING,
      processingStartedAt: nowIso(),
      lastErrorMessage: null,
      errorMessage: null,
    };
    await updateMediaItem(processing);
    logSync("media-processing", processing);
    try {
      const response = await fetchWithTimeout("/api/driver/media/uploads", {
        method: "POST",
        headers: {
          "content-type": processing.mimeType,
          "x-media-kind": processing.mediaKind,
          "x-owner-type": processing.ownerType || "DRIVER_DRAFT",
          "x-owner-id": processing.id,
          "x-fleet-order-id": processing.fleetOrderId || "",
          "x-stop-key": processing.stopKey || "",
          "x-image-width": processing.width || "",
          "x-image-height": processing.height || "",
          "x-client-upload-key": processing.idempotencyKey || processing.id,
          "x-capture-metadata": encodeCaptureMetadataHeader(processing.stampMetadata),
        },
        body: processing.file,
      });
      const payload = await parseErrorPayload(response);
      if (!response.ok) {
        const failure = classifySyncFailure({
          status: response.status,
          code: payload.code,
          message: payload.message || `HTTP ${response.status}`,
          attempt: attempts.attemptCount,
        });
        await updateMediaItem({
          ...processing,
          ...failure,
          lastHttpStatus: response.status,
          lastErrorCode: payload.code || null,
          errorMessage: failure.lastErrorMessage,
          processingStartedAt: null,
          correlationId: payload.correlationId || response.headers.get("x-correlation-id") || null,
        });
        logSync("media-failed", processing, { httpStatus: response.status, errorCategory: failure.errorCategory });
        if (response.status === 401) requestSessionRecovery();
        if (failure.syncStatus === SYNC_STATE.RETRY_SCHEDULED || response.status === 401) break;
        continue;
      }
      const serverMediaId = payload.media?.id;
      if (!serverMediaId) throw new Error("Server tidak mengembalikan identitas foto.");

      if (processing.originalFile instanceof Blob && Number(processing.originalFile.size || 0) > 0) {
        const originalResponse = await fetchWithTimeout(
          `/api/driver/media/${encodeURIComponent(serverMediaId)}/original`,
          {
            method: "POST",
            headers: {
              "content-type": processing.originalMimeType || processing.originalFile.type || "image/jpeg",
              "x-client-upload-key": processing.idempotencyKey || processing.id,
              "x-original-width": processing.originalWidth || "",
              "x-original-height": processing.originalHeight || "",
            },
            body: processing.originalFile,
          },
        );
        const originalPayload = await parseErrorPayload(originalResponse);
        if (!originalResponse.ok) {
          const failure = classifySyncFailure({
            status: originalResponse.status,
            code: originalPayload.code,
            message: originalPayload.message || `HTTP ${originalResponse.status}`,
            attempt: attempts.attemptCount,
          });
          await updateMediaItem({
            ...processing,
            serverMediaId,
            ...failure,
            lastHttpStatus: originalResponse.status,
            lastErrorCode: originalPayload.code || null,
            errorMessage: failure.lastErrorMessage,
            processingStartedAt: null,
            correlationId: originalPayload.correlationId || originalResponse.headers.get("x-correlation-id") || null,
          });
          logSync("media-original-failed", processing, {
            httpStatus: originalResponse.status,
            errorCategory: failure.errorCategory,
          });
          if (originalResponse.status === 401) requestSessionRecovery();
          if (failure.syncStatus === SYNC_STATE.RETRY_SCHEDULED || originalResponse.status === 401) break;
          continue;
        }
      }

      mappings.set(processing.id, serverMediaId);
      await updateMediaItem({
        ...processing,
        serverMediaId,
        originalSynced: processing.originalFile instanceof Blob ? true : null,
        syncStatus: SYNC_STATE.SYNCED,
        recoverability: null,
        errorCategory: null,
        lastErrorMessage: null,
        errorMessage: null,
        lastHttpStatus: response.status,
        lastErrorCode: null,
        nextAttemptAt: null,
        processingStartedAt: null,
        syncedAt: nowIso(),
      });
      logSync("media-synced", processing, { httpStatus: response.status });
    } catch (error) {
      const failure = classifySyncFailure({
        message: error?.name === "AbortError" || error?.name === "TimeoutError"
          ? "Pengiriman foto terlalu lama dan akan dicoba lagi."
          : "Koneksi terputus saat mengirim foto.",
        attempt: attempts.attemptCount,
      });
      await updateMediaItem({
        ...processing,
        ...failure,
        errorMessage: failure.lastErrorMessage,
        processingStartedAt: null,
      });
      logSync("media-network-failed", processing, { errorCategory: failure.errorCategory });
      break;
    }
  }
  emitChange();
  return { mappings, ...(await driverQueueStatus()) };
}

export async function syncPendingDriverMedia(options = {}) {
  if (activeMediaPromise) return activeMediaPromise;
  const run = () => processPendingDriverMedia(options);
  activeMediaPromise = Promise.resolve(
    globalThis.navigator?.locks?.request
      ? navigator.locks.request(MEDIA_LOCK, { mode: "exclusive" }, run)
      : run(),
  ).finally(() => { activeMediaPromise = null; });
  return activeMediaPromise;
}

async function processDriverQueue(options = {}) {
  if (!online()) return { synced: 0, ...(await driverQueueStatus()) };
  const mediaSync = await syncPendingDriverMedia({ ...options, onlyId: null });
  const remainingMedia = await listDriverMedia();
  const unresolvedMediaIds = new Set(
    remainingMedia.filter((item) => item.syncStatus !== SYNC_STATE.SYNCED).map((item) => item.id),
  );
  const failedMediaIds = new Set(
    remainingMedia
      .filter((item) => isFailedSyncState(item.syncStatus) || item.syncStatus === SYNC_STATE.SUPERSEDED)
      .map((item) => item.id),
  );
  const items = await listDriverQueue();
  let synced = 0;
  const blockingByFleetOrder = new Map();
  for (const item of items) {
    if (item.syncStatus !== SYNC_STATE.FAILED_PERMANENT) continue;
    if (!["REORDER_STOPS", "SAVE_LOADING"].includes(item.action)) continue;
    const fleetOrderId = String(item.fleetOrderId || item.payload?.fleetOrderId || "");
    if (fleetOrderId && !blockingByFleetOrder.has(fleetOrderId)) blockingByFleetOrder.set(fleetOrderId, item);
  }

  for (const item of items.filter((entry) => shouldAttempt(entry, options))) {
    const fleetOrderId = String(item.fleetOrderId || item.payload?.fleetOrderId || "");
    const blockingItem = fleetOrderId ? blockingByFleetOrder.get(fleetOrderId) : null;
    if (blockingItem && blockingItem.id !== item.id && String(item.createdAt || "") >= String(blockingItem.createdAt || "")) {
      await updateQueueItem({
        ...item,
        syncStatus: SYNC_STATE.FAILED_RECOVERABLE,
        recoverability: "REQUIRES_REVIEW",
        errorCategory: "DEPENDENCY",
        lastErrorCode: "DRIVER_SYNC_DEPENDENCY_BLOCKED",
        lastErrorMessage: "Selesaikan konflik urutan rute atau checklist sebelumnya sebelum mengirim data ini.",
        errorMessage: "Selesaikan konflik urutan rute atau checklist sebelumnya sebelum mengirim data ini.",
        nextAttemptAt: null,
        processingStartedAt: null,
      });
      continue;
    }
    const resolvedPayload = replaceMediaReferences(item.payload, mediaSync.mappings);
    if (payloadContainsReference(resolvedPayload, unresolvedMediaIds)) {
      const blockedByFailure = payloadContainsReference(resolvedPayload, failedMediaIds);
      const nextAttemptAt = blockedByFailure ? null : new Date(Date.now() + 5_000).toISOString();
      await updateQueueItem({
        ...item,
        payload: resolvedPayload,
        syncStatus: blockedByFailure ? SYNC_STATE.FAILED_RECOVERABLE : SYNC_STATE.RETRY_SCHEDULED,
        recoverability: blockedByFailure ? "MEDIA_REVIEW_REQUIRED" : "AUTOMATIC",
        errorCategory: "MEDIA_DEPENDENCY",
        lastErrorMessage: blockedByFailure
          ? "Foto terkait perlu diperiksa sebelum data ini dapat dikirim."
          : "Menunggu foto selesai dikirim.",
        errorMessage: blockedByFailure
          ? "Foto terkait perlu diperiksa sebelum data ini dapat dikirim."
          : "Menunggu foto selesai dikirim.",
        nextAttemptAt,
        processingStartedAt: null,
      });
      continue;
    }

    const attempts = attemptFields(item);
    const processing = {
      ...item,
      ...attempts,
      payload: resolvedPayload,
      syncStatus: SYNC_STATE.PROCESSING,
      processingStartedAt: nowIso(),
      lastErrorMessage: null,
      errorMessage: null,
    };
    await updateQueueItem(processing);
    logSync("operation-processing", processing);
    try {
      const response = await fetchWithTimeout(item.path, {
        method: item.method,
        headers: { "content-type": "application/json" },
        body: JSON.stringify(resolvedPayload),
      });
      if (response.ok) {
        await deleteQueueItem(item.id);
        if (item.action === "REORDER_STOPS" && item.fleetOrderId) {
          await clearDriverRouteDraft(item.fleetOrderId, { idempotencyKey: item.idempotencyKey });
        }
        synced += 1;
        logSync("operation-synced", processing, { httpStatus: response.status });
        continue;
      }
      const payload = await parseErrorPayload(response);
      const failure = classifySyncFailure({
        status: response.status,
        code: payload.code,
        message: payload.message || `HTTP ${response.status}`,
        attempt: attempts.attemptCount,
      });
      await updateQueueItem({
        ...processing,
        ...failure,
        lastHttpStatus: response.status,
        lastErrorCode: payload.code || null,
        errorMessage: failure.lastErrorMessage,
        processingStartedAt: null,
        correlationId: payload.correlationId || response.headers.get("x-correlation-id") || null,
      });
      if (item.action === "REORDER_STOPS" && item.fleetOrderId && response.status === 409) {
        await markDriverRouteDraftConflict(item.fleetOrderId, {
          idempotencyKey: item.idempotencyKey,
          lastErrorCode: payload.code || "DRIVER_ROUTE_VERSION_CONFLICT",
          lastErrorMessage: payload.message || failure.lastErrorMessage,
        });
      }
      logSync("operation-failed", processing, { httpStatus: response.status, errorCategory: failure.errorCategory });
      if (failure.syncStatus === SYNC_STATE.FAILED_PERMANENT
        && ["REORDER_STOPS", "SAVE_LOADING"].includes(item.action)
        && fleetOrderId) {
        blockingByFleetOrder.set(fleetOrderId, item);
      }
      if (response.status === 401) requestSessionRecovery();
      if (failure.syncStatus === SYNC_STATE.RETRY_SCHEDULED || response.status === 401) break;
    } catch (error) {
      const failure = classifySyncFailure({
        message: error?.name === "AbortError" || error?.name === "TimeoutError"
          ? "Permintaan terlalu lama dan akan dicoba lagi."
          : "Koneksi terputus saat mengirim data.",
        attempt: attempts.attemptCount,
      });
      await updateQueueItem({
        ...processing,
        ...failure,
        errorMessage: failure.lastErrorMessage,
        processingStartedAt: null,
      });
      logSync("operation-network-failed", processing, { errorCategory: failure.errorCategory });
      break;
    }
  }
  emitChange();
  return { synced, ...(await driverQueueStatus()) };
}

export async function flushDriverQueue(options = {}) {
  if (activeFlushPromise) return activeFlushPromise;
  const run = () => processDriverQueue(options);
  activeFlushPromise = Promise.resolve(
    globalThis.navigator?.locks?.request
      ? navigator.locks.request(QUEUE_LOCK, { mode: "exclusive" }, run)
      : run(),
  ).finally(() => { activeFlushPromise = null; });
  return activeFlushPromise;
}

export async function storePendingDriverMedia(file, mediaKind, metadata = {}) {
  if (!file || typeof file.size !== "number" || file.size <= 0) {
    throw new Error("File foto kosong atau tidak dapat dibaca.");
  }
  const originalFile = metadata.originalFile instanceof Blob ? metadata.originalFile : null;
  await ensureDriverMediaCapacity(file.size + Number(originalFile?.size || 0));
  const id = globalThis.crypto?.randomUUID?.() || `media-${Date.now()}`;
  const timestamp = nowIso();
  const item = normalizeLegacySyncItem({
    id,
    localTransactionId: id,
    idempotencyKey: id,
    mediaKind,
    file,
    mimeType: file.type || "application/octet-stream",
    byteSize: file.size,
    originalFile,
    originalMimeType: originalFile?.type || null,
    originalByteSize: Number(originalFile?.size || 0) || null,
    originalWidth: metadata.originalWidth || null,
    originalHeight: metadata.originalHeight || null,
    stampMetadata: metadata.stampMetadata || null,
    ownerType: metadata.ownerType || "DRIVER_DRAFT",
    fleetOrderId: metadata.fleetOrderId || null,
    stopKey: metadata.stopKey || null,
    width: metadata.width || null,
    height: metadata.height || null,
    createdAt: timestamp,
    updatedAt: timestamp,
    syncStatus: SYNC_STATE.PENDING,
    attemptCount: 0,
  }, { kind: "media", now: timestamp });
  try {
    await transaction("media", "readwrite", (store) => store.put(item));
  } catch (error) {
    if (isQuotaExceededError(error)) throw storageCapacityError(error);
    throw error;
  }
  logSync("media-queued", item);
  emitChange();
  return { id, mimeType: item.mimeType, byteSize: item.byteSize, local: true };
}


export async function listDriverStopCompletionMedia({ fleetOrderId, stopKey } = {}) {
  const safeFleetOrderId = String(fleetOrderId || "").trim();
  const safeStopKey = String(stopKey || "").trim();
  if (!safeFleetOrderId || !safeStopKey) return [];
  return (await listDriverMedia()).filter((item) => item.mediaKind === "STOP_COMPLETION"
    && String(item.fleetOrderId || "") === safeFleetOrderId
    && String(item.stopKey || "") === safeStopKey
    && item.syncStatus !== SYNC_STATE.SUPERSEDED);
}

export async function repairDriverMediaContext(id, { fleetOrderId, stopKey, ownerType = "DELIVERY_STOP" } = {}) {
  const safeFleetOrderId = String(fleetOrderId || "").trim();
  const safeStopKey = String(stopKey || "").trim();
  if (!safeFleetOrderId || !safeStopKey) {
    throw new Error("Fleet Order dan titik aktif wajib tersedia untuk memperbaiki foto.");
  }
  const items = await listDriverMedia();
  const item = items.find((entry) => entry.id === id);
  if (!item) throw new Error("Foto lokal tidak ditemukan.");
  if (item.mediaKind !== "STOP_COMPLETION") {
    throw new Error("Hanya foto bukti delivery yang dapat diperbaiki dengan titik aktif.");
  }
  if (item.fleetOrderId && String(item.fleetOrderId) !== safeFleetOrderId) {
    throw new Error("Foto ini sudah terhubung ke Fleet Order lain.");
  }
  if (item.stopKey && String(item.stopKey) !== safeStopKey) {
    throw new Error("Foto ini sudah terhubung ke titik pengiriman lain.");
  }
  if ([SYNC_STATE.SYNCED, SYNC_STATE.SUPERSEDED].includes(item.syncStatus)) return item;

  const repaired = {
    ...item,
    ownerType,
    fleetOrderId: safeFleetOrderId,
    stopKey: safeStopKey,
    syncStatus: SYNC_STATE.PENDING,
    recoverability: "AUTOMATIC",
    errorCategory: null,
    lastErrorMessage: null,
    errorMessage: null,
    lastHttpStatus: null,
    lastErrorCode: null,
    nextAttemptAt: nowIso(),
    processingStartedAt: null,
    attemptCount: 0,
    retryCount: 0,
    contextRepairedAt: nowIso(),
  };
  await updateMediaItem(repaired);
  emitChange();
  return repaired;
}

export async function getDriverSyncItems() {
  const [queue, media] = await Promise.all([listDriverQueue(), listDriverMedia()]);
  return [
    ...queue.map((item) => ({ ...item, kind: "queue", displayType: item.action || "DATA", failureLabel: syncFailureLabel(item) })),
    ...media.map((item) => ({ ...item, kind: "media", displayType: item.mediaKind || "PHOTO", failureLabel: syncFailureLabel(item) })),
  ].filter((item) => item.syncStatus !== SYNC_STATE.SYNCED && item.syncStatus !== SYNC_STATE.SUPERSEDED)
    .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
}

export async function resolveDriverMediaReferences(payload) {
  const media = await listDriverMedia();
  const mappings = new Map(
    media
      .filter((item) => item.syncStatus === SYNC_STATE.SYNCED && item.serverMediaId)
      .map((item) => [item.id, item.serverMediaId]),
  );
  const unresolvedIds = new Set(
    media
      .filter((item) => item.syncStatus !== SYNC_STATE.SYNCED)
      .map((item) => item.id),
  );
  return {
    payload: replaceMediaReferences(payload, mappings),
    hasUnsynced: payloadContainsReference(payload, unresolvedIds),
  };
}

export async function payloadHasUnsyncedDriverMedia(payload) {
  return (await resolveDriverMediaReferences(payload)).hasUnsynced;
}

async function resetForManualRetry(item, kind) {
  const update = kind === "media" ? updateMediaItem : updateQueueItem;
  await update({
    ...item,
    syncStatus: SYNC_STATE.PENDING,
    recoverability: "AUTOMATIC",
    errorCategory: null,
    lastErrorMessage: null,
    errorMessage: null,
    nextAttemptAt: nowIso(),
    processingStartedAt: null,
    attemptCount: 0,
    retryCount: 0,
    manualRetryCount: Number(item.manualRetryCount || 0) + 1,
  });
}

export async function retryDriverSyncItem(kind, id) {
  const items = kind === "media" ? await listDriverMedia() : await listDriverQueue();
  const item = items.find((entry) => entry.id === id);
  if (!item) return driverQueueStatus();
  if (item.syncStatus === SYNC_STATE.FAILED_PERMANENT) {
    throw new Error("Data ini perlu diperiksa atau dihapus dengan konfirmasi sebelum dapat dilanjutkan.");
  }
  await resetForManualRetry(item, kind);
  emitChange();
  return kind === "media"
    ? syncPendingDriverMedia({ onlyId: id, force: true, includeRecoverable: true })
    : flushDriverQueue({ onlyId: id, force: true, includeRecoverable: true });
}

export async function retryAllRecoverableDriverSync() {
  const [queue, media] = await Promise.all([listDriverQueue(), listDriverMedia()]);
  for (const item of media.filter((entry) => entry.syncStatus !== SYNC_STATE.FAILED_PERMANENT && entry.syncStatus !== SYNC_STATE.SYNCED)) {
    await resetForManualRetry(item, "media");
  }
  for (const item of queue.filter((entry) => entry.syncStatus !== SYNC_STATE.FAILED_PERMANENT && entry.syncStatus !== SYNC_STATE.SYNCED)) {
    await resetForManualRetry(item, "queue");
  }
  emitChange();
  return flushDriverQueue({ force: true, includeRecoverable: true, source: "manual" });
}

export async function supersedeDriverSyncItem(kind, id) {
  const items = kind === "media" ? await listDriverMedia() : await listDriverQueue();
  const item = items.find((entry) => entry.id === id);
  if (!item) return driverQueueStatus();
  const update = kind === "media" ? updateMediaItem : updateQueueItem;
  await update({
    ...item,
    syncStatus: SYNC_STATE.SUPERSEDED,
    recoverability: null,
    nextAttemptAt: null,
    processingStartedAt: null,
    supersededAt: nowIso(),
  });
  if (item.action === "REORDER_STOPS" && item.fleetOrderId) {
    await clearDriverRouteDraft(item.fleetOrderId, { idempotencyKey: item.idempotencyKey });
  }
  emitChange();
  return driverQueueStatus();
}

export function supersedeDriverMedia(id) {
  return supersedeDriverSyncItem("media", id);
}

export function subscribeDriverSync(listener) {
  globalThis.addEventListener?.(CHANGE_EVENT, listener);
  return () => globalThis.removeEventListener?.(CHANGE_EVENT, listener);
}

export function subscribeDriverSessionRecovery(listener) {
  globalThis.addEventListener?.(SESSION_RECOVERY_EVENT, listener);
  return () => globalThis.removeEventListener?.(SESSION_RECOVERY_EVENT, listener);
}

export async function driverQueueStatus() {
  const [items, media] = await Promise.all([listDriverQueue(), listDriverMedia()]);
  const retryTimes = [...items, ...media]
    .map((item) => {
      if (item.syncStatus === SYNC_STATE.RETRY_SCHEDULED && item.nextAttemptAt) {
        return new Date(item.nextAttemptAt).getTime();
      }
      if (item.syncStatus === SYNC_STATE.PROCESSING) {
        const startedAt = new Date(item.processingStartedAt || item.lastAttemptAt || 0).getTime();
        return Number.isFinite(startedAt) ? startedAt + PROCESSING_STALE_MS : Date.now();
      }
      return Number.NaN;
    })
    .filter(Number.isFinite);
  return {
    pending: items.filter((item) => isPendingSyncState(item.syncStatus)).length,
    failed: items.filter((item) => isFailedSyncState(item.syncStatus)).length,
    mediaPending: media.filter((item) => isPendingSyncState(item.syncStatus)).length,
    mediaFailed: media.filter((item) => isFailedSyncState(item.syncStatus)).length,
    recoverable: [...items, ...media].filter((item) => item.syncStatus === SYNC_STATE.FAILED_RECOVERABLE).length,
    permanent: [...items, ...media].filter((item) => item.syncStatus === SYNC_STATE.FAILED_PERMANENT).length,
    processing: [...items, ...media].filter((item) => item.syncStatus === SYNC_STATE.PROCESSING).length,
    nextRetryAt: retryTimes.length ? new Date(Math.min(...retryTimes)).toISOString() : null,
  };
}
